// TODO: access `BlockType` and `ArgumentType` without reaching into VM
// Should we move these into a new extension support module or something?
import ArgumentType from 'openblock-vm/src/extension-support/argument-type';
import BlockType from 'openblock-vm/src/extension-support/block-type';

/**
 * Define a block using extension info which has the ability to dynamically determine (and update) its layout.
 * This functionality is used for extension blocks which can change its properties based on different state
 * information. For example, the `control_stop` block changes its shape based on which menu item is selected
 * and a variable block changes its text to reflect the variable name without using an editable field.
 * @param {object} ScratchBlocks - The ScratchBlocks name space.
 * @param {object} categoryInfo - Information about this block's extension category, including any menus and icons.
 * @param {object} staticBlockInfo - The base block information before any dynamic changes.
 * @param {string} extendedOpcode - The opcode for the block (including the extension ID).
 */
// TODO: grow this until it can fully replace `_convertForScratchBlocks` in the VM runtime
const defineDynamicBlock = (ScratchBlocks, categoryInfo, staticBlockInfo, extendedOpcode) => ({
    init: function () {
        // Verifica se é a categoria "Pinos" e altera a cor para #3050FF (cor do header)
        const categoryId = categoryInfo.id || '';
        const categoryName = (categoryInfo.name || '').toLowerCase();
        const isPinsCategory = (
            categoryId.includes('pins') || 
            categoryId.includes('arduinoUno.pins') ||
            categoryId.includes('arduinoMega2560.pins') ||
            (categoryId.includes('arduinoUno') && categoryId.includes('pin')) ||
            (categoryId.includes('arduinoMega2560') && categoryId.includes('pin')) ||
            categoryName.includes('pins') ||
            categoryName.includes('pino') ||
            (extendedOpcode && extendedOpcode.startsWith('arduino_pin_'))
        );
        
        // Define as cores: se for Pinos, usa #3050FF, senão usa as cores originais
        let color1 = categoryInfo.color1;
        let color2 = categoryInfo.color2;
        let color3 = categoryInfo.color3;
        
        if (isPinsCategory) {
            color1 = '#3050FF'; // Cor principal do header
            color2 = '#2040E6'; // Versão mais escura para secondary
            color3 = '#1020CC'; // Versão ainda mais escura para tertiary
        }
        
        const blockJson = {
            type: extendedOpcode,
            inputsInline: true,
            category: categoryInfo.name,
            colour: color1,
            colourSecondary: color2,
            colourTertiary: color3
        };
        // There is a openblock-blocks / Blockly extension called "scratch_extension" which adjusts the styling of
        // blocks to allow for an icon, a feature of Scratch extension blocks. However, Scratch "core" extension
        // blocks don't have icons and so they should not use 'scratch_extension'. Adding a openblock-blocks / Blockly
        // extension after `jsonInit` isn't fully supported (?), so we decide now whether there will be an icon.
        if (staticBlockInfo.blockIconURI || categoryInfo.blockIconURI) {
            blockJson.extensions = ['scratch_extension'];
        }
        // initialize the basics of the block, to be overridden & extended later by `domToMutation`
        this.jsonInit(blockJson);
        // initialize the cached block info used to carry block info from `domToMutation` to `mutationToDom`
        this.blockInfoText = '{}';
        // we need a block info update (through `domToMutation`) before we have a completely initialized block
        this.needsBlockInfoUpdate = true;
    },
    mutationToDom: function () {
        const container = document.createElement('mutation');
        container.setAttribute('blockInfo', this.blockInfoText);
        return container;
    },
    domToMutation: function (xmlElement) {
        const blockInfoText = xmlElement.getAttribute('blockInfo') || this.blockInfoText;
        if (!blockInfoText || blockInfoText === '{}') return;
        
        // Permite atualização mesmo se já foi inicializado (para atualizar traduções)
        const isInitialUpdate = this.needsBlockInfoUpdate;
        if (isInitialUpdate) {
        delete this.needsBlockInfoUpdate;
        }
        
        this.blockInfoText = blockInfoText;
        const blockInfo = JSON.parse(blockInfoText);
        
        // Salva o blockInfo original para referência
        const originalBlockInfo = {...blockInfo};

        switch (blockInfo.blockType) {
        case BlockType.COMMAND:
        case BlockType.CONDITIONAL:
        case BlockType.LOOP:
            this.setOutputShape(ScratchBlocks.OUTPUT_SHAPE_SQUARE);
            this.setPreviousStatement(true);
            this.setNextStatement(!blockInfo.isTerminal);
            break;
        case BlockType.REPORTER:
            this.setOutput(true);
            this.setOutputShape(ScratchBlocks.OUTPUT_SHAPE_ROUND);
            if (!blockInfo.disableMonitor) {
                this.setCheckboxInFlyout(true);
            }
            break;
        case BlockType.BOOLEAN:
            this.setOutput(true);
            this.setOutputShape(ScratchBlocks.OUTPUT_SHAPE_HEXAGONAL);
            break;
        case BlockType.HAT:
        case BlockType.EVENT:
            this.setOutputShape(ScratchBlocks.OUTPUT_SHAPE_SQUARE);
            this.setNextStatement(true);
            break;
        }

        // Verifica novamente se é categoria Pinos para atualizar a cor durante renderização
        const categoryId = categoryInfo.id || '';
        const categoryName = (categoryInfo.name || '').toLowerCase();
        const isPinsCategory = (
            categoryId.includes('pins') || 
            categoryId.includes('arduinoUno.pins') ||
            categoryId.includes('arduinoMega2560.pins') ||
            (categoryId.includes('arduinoUno') && categoryId.includes('pin')) ||
            (categoryId.includes('arduinoMega2560') && categoryId.includes('pin')) ||
            categoryName.includes('pins') ||
            categoryName.includes('pino') ||
            (extendedOpcode && extendedOpcode.startsWith('arduino_pin_'))
        );
        
        if (isPinsCategory) {
            // Usa a cor do header para categoria Pinos
            this.setColour('#3050FF', '#2040E6', '#1020CC');
        } else if (blockInfo.color1 || blockInfo.color2 || blockInfo.color3) {
            // `setColour` handles undefined parameters by adjusting defined colors
            this.setColour(blockInfo.color1, blockInfo.color2, blockInfo.color3);
        }

        // Layout block arguments
        // TODO handle E/C Blocks
        let blockText = blockInfo.text;
        let translationKey = null;
        let translatedText = null;
        
        // Mapeamento direto usando regex para corresponder textos com valores reais
        const originalText = blockInfo.text;
        let matched = false;
        
        // Padrões de tradução usando regex para corresponder textos com valores
        const translationPatterns = [
            // Blocos de pinos - padrões com valores reais
            {pattern: /^set pin (\d+) mode (input|output|input-pullup)$/i, replace: 'definir pino $1 modo $2'},
            {pattern: /^set digital pin (\d+) out (high|low)$/i, replace: 'definir pino digital $1 saída $2'},
            {pattern: /^set pwm pin (\d+) out (\d+)$/i, replace: 'definir pino pwm $1 saída $2'},
            {pattern: /^set servo pin (\d+) out (\d+)$/i, replace: 'definir pino servo $1 saída $2'},
            {pattern: /^read digital pin (\d+)$/i, replace: 'ler pino digital $1'},
            {pattern: /^read analog pin (A\d+|\d+)$/i, replace: 'ler pino analógico $1'},
            {pattern: /^attach interrupt pin (\d+) mode (rising edge|falling edge|change edge|low) executes$/i, replace: 'anexar interrupção pino $1 modo $2 executa'},
            {pattern: /^detach interrupt pin (\d+)$/i, replace: 'desanexar interrupção pino $1'},
            // Blocos com placeholders [PIN], [MODE], etc.
            {pattern: /^set pin \[PIN\] mode \[MODE\]$/i, replace: 'definir pino [PIN] modo [MODE]'},
            {pattern: /^set digital pin \[PIN\] out \[LEVEL\]$/i, replace: 'definir pino digital [PIN] saída [LEVEL]'},
            {pattern: /^set pwm pin \[PIN\] out \[OUT\]$/i, replace: 'definir pino pwm [PIN] saída [OUT]'},
            {pattern: /^set servo pin \[PIN\] out \[OUT\]$/i, replace: 'definir pino servo [PIN] saída [OUT]'},
            {pattern: /^read digital pin \[PIN\]$/i, replace: 'ler pino digital [PIN]'},
            {pattern: /^read analog pin \[PIN\]$/i, replace: 'ler pino analógico [PIN]'},
            {pattern: /^attach interrupt pin \[PIN\] mode \[MODE\] executes$/i, replace: 'anexar interrupção pino [PIN] modo [MODE] executa'},
            {pattern: /^detach interrupt pin \[PIN\]$/i, replace: 'desanexar interrupção pino [PIN]'},
            // Blocos serial Uno
            {pattern: /^serial begin baudrate (\d+)$/i, replace: 'serial iniciar taxa de transmissão $1'},
            {pattern: /^serial begin baudrate \[VALUE\]$/i, replace: 'serial iniciar taxa de transmissão [VALUE]'},
            {pattern: /^serial print \[VALUE\] \[EOL\]$/i, replace: 'serial imprimir [VALUE] [EOL]'},
            {pattern: /^serial read a byte$/i, replace: 'serial ler um byte'},
            {pattern: /^serial available data length$/i, replace: 'serial comprimento de dados disponível'},
            // Blocos serial Mega (multiSerial)
            {pattern: /^serial (\d+) begin baudrate (\d+)$/i, replace: 'serial $1 iniciar taxa de transmissão $2'},
            {pattern: /^serial \[NO\] begin baudrate \[VALUE\]$/i, replace: 'serial [NO] iniciar taxa de transmissão [VALUE]'},
            {pattern: /^serial (\d+) print (.+) (warp|no warp)$/i, replace: 'serial $1 imprimir $2 $3'},
            {pattern: /^serial \[NO\] print \[VALUE\] \[EOL\]$/i, replace: 'serial [NO] imprimir [VALUE] [EOL]'},
            {pattern: /^serial (\d+) available data length$/i, replace: 'serial $1 comprimento de dados disponível'},
            {pattern: /^serial \[NO\] available data length$/i, replace: 'serial [NO] comprimento de dados disponível'},
            {pattern: /^serial (\d+) read a byte$/i, replace: 'serial $1 ler um byte'},
            {pattern: /^serial \[NO\] read a byte$/i, replace: 'serial [NO] ler um byte'},
            // Blocos de dados
            {pattern: /^constrain \[DATA\] between \(\[ARG0\], \[ARG1\]\)$/i, replace: 'limitar [DATA] entre ([ARG0], [ARG1])'},
            {pattern: /^convert \[DATA\] to \[TYPE\]$/i, replace: 'converter [DATA] para [TYPE]'},
            {pattern: /^convert \[DATA\] to ASCII character$/i, replace: 'converter [DATA] para caractere ASCII'},
            {pattern: /^convert \[DATA\] to ASCII nubmer$/i, replace: 'converter [DATA] para número ASCII'},
            {pattern: /^map \[DATA\] from \(\[ARG0\], \[ARG1\]\) to \(\[ARG2\], \[ARG3\]\)$/i, replace: 'mapear [DATA] de ([ARG0], [ARG1]) para ([ARG2], [ARG3])'}
        ];
        
        // Tenta corresponder com padrões primeiro
        for (const {pattern, replace} of translationPatterns) {
            if (pattern.test(originalText)) {
                blockText = originalText.replace(pattern, replace);
                // Traduz valores específicos
                blockText = blockText.replace(/input/gi, 'entrada');
                blockText = blockText.replace(/output/gi, 'saída');
                blockText = blockText.replace(/input-pullup/gi, 'entrada-pullup');
                blockText = blockText.replace(/high/gi, 'alto');
                blockText = blockText.replace(/low/gi, 'baixo');
                blockText = blockText.replace(/rising edge/gi, 'borda de subida');
                blockText = blockText.replace(/falling edge/gi, 'borda de descida');
                blockText = blockText.replace(/change edge/gi, 'mudar borda');
                translatedText = blockText;
                translationKey = 'pattern';
                matched = true;
                break;
            }
        }
        
        if (!matched && ScratchBlocks && ScratchBlocks.ScratchMsgs && ScratchBlocks.ScratchMsgs.locales) {
            // Se não encontrou tradução direta, tenta usar o sistema de traduções
            const currentLocale = ScratchBlocks.ScratchMsgs.currentLocale || 'en';
            const translations = ScratchBlocks.ScratchMsgs.locales[currentLocale] || {};
            const ptBrTranslations = ScratchBlocks.ScratchMsgs.locales['pt-br'] || {};
            // Para blocos Arduino, sempre prioriza traduções pt-br se disponíveis
            // Isso garante que os blocos sejam traduzidos mesmo quando o locale é 'en'
            const allTranslations = currentLocale === 'pt-br' 
                ? {...translations, ...ptBrTranslations}
                : {...ptBrTranslations, ...translations};
            
            // Tenta encontrar a tradução usando o opcode
            const extendedOpcode = this.type; // O opcode completo do bloco (ex: arduino_pin_setPinMode)
            
            // Tenta diferentes formatos de chave de tradução
            const possibleKeys = [
                extendedOpcode, // arduino_pin_setPinMode
                extendedOpcode.replace(/_/g, '.'), // arduino.pin.setPinMode
                extendedOpcode.replace(/^arduino_/, 'arduinoUno.'), // arduinoUno.pin.setPinMode
                extendedOpcode.replace(/^arduino_pin_/, 'arduinoUno.pins.'), // arduinoUno.pins.setPinMode
                extendedOpcode.replace(/^arduino_pin_/, 'arduinoMega2560.pins.'), // arduinoMega2560.pins.setPinMode
                extendedOpcode.replace(/^arduino_serial_/, 'arduinoMega2560.serial.'), // Mega multiSerial
                extendedOpcode.replace(/^arduino_serial_/, 'arduinoUno.serial.'), // Uno serial
                extendedOpcode.replace(/^arduino_data_/, 'arduinoMega2560.data.'),
                extendedOpcode.replace(/^arduino_data_/, 'arduinoUno.data.')
            ];
            
            for (const key of possibleKeys) {
                if (allTranslations[key]) {
                    translatedText = allTranslations[key];
                    translationKey = key;
                    break;
                }
            }
            
            // Se encontrou tradução, usa ela diretamente
            if (translationKey && translatedText) {
                blockText = translatedText;
            }
        }
        
        
        
        // Se o texto foi traduzido, atualiza o blockInfo para persistir a tradução
        if (blockText !== originalBlockInfo.text && translationKey) {
            // Salva o texto traduzido no blockInfo para que seja persistido
            blockInfo.text = blockText;
            // Atualiza o blockInfoText com o texto traduzido
            this.blockInfoText = JSON.stringify(blockInfo);
        }
        
        const args = [];
        let argCount = 0;
        
        // Converte placeholders [ARG] para formato ScratchBlocks %1, %2, etc.
        // Mas primeiro, preserva os valores reais que já estão no texto
        let scratchBlocksStyleText = blockText;
        
        // Se o texto tem placeholders [PIN], [MODE], etc., converte para %1, %2
        if (blockText.includes('[') && blockText.includes(']')) {
            scratchBlocksStyleText = blockText.replace(/\[(.+?)]/g, (match, argName) => {
                const arg = blockInfo.arguments && blockInfo.arguments[argName];
                if (arg) {
            switch (arg.type) {
            case ArgumentType.STRING:
                args.push({type: 'input_value', name: argName});
                break;
            case ArgumentType.BOOLEAN:
                args.push({type: 'input_value', name: argName, check: 'Boolean'});
                break;
                    case ArgumentType.NUMBER:
                        args.push({type: 'input_value', name: argName, check: 'Number'});
                        break;
                    }
            return `%${++argCount}`;
                }
                // Se não encontrar o argumento, mantém o placeholder
                return match;
            });
        } else {
            // Se o texto não tem placeholders, pode ter valores reais que precisam ser convertidos
            // Neste caso, precisamos reconstruir o texto com placeholders baseado nos argumentos
            if (blockInfo.arguments) {
                const argNames = Object.keys(blockInfo.arguments);
                let tempText = blockText;
                argNames.forEach((argName, index) => {
                    const arg = blockInfo.arguments[argName];
                    // Tenta encontrar o valor no texto e substituir por placeholder
                    // Isso é complexo, então vamos usar uma abordagem diferente
                    args.push({type: 'input_value', name: argName});
                    tempText = tempText.replace(/\d+/g, (match, offset) => {
                        // Tenta mapear valores para argumentos (heurística simples)
                        return `%${index + 1}`;
                    });
                });
                // Por enquanto, vamos manter o texto como está e usar field_static
                // Vamos tentar uma abordagem mais simples: usar o texto traduzido diretamente
            }
        }
        
        // Se não conseguiu converter placeholders, tenta usar o texto diretamente
        if (args.length === 0 && blockInfo.arguments) {
            // Usa field_static para texto fixo ou tenta extrair argumentos do texto
            const argNames = Object.keys(blockInfo.arguments);
            argNames.forEach((argName, index) => {
                const arg = blockInfo.arguments[argName];
                args.push({type: 'input_value', name: argName});
            });
            // Reconstrói o texto com placeholders
            scratchBlocksStyleText = blockText;
            // Substitui valores numéricos e palavras-chave por placeholders
            let placeholderIndex = 1;
            argNames.forEach(argName => {
                scratchBlocksStyleText = scratchBlocksStyleText.replace(/\b(\d+|input|output|high|low)\b/, `%${placeholderIndex++}`);
            });
        }
        
        this.interpolate_(scratchBlocksStyleText, args);
        
        // Força tradução após renderização usando um hook de atualização
        // Isso garante que o texto seja traduzido mesmo se o interpolate_ não funcionar
        if (blockText !== originalBlockInfo.text) {
            // Agenda uma atualização após o bloco ser renderizado
            setTimeout(() => {
                if (this.text_ && this.text_.textContent) {
                    // Traduz o texto diretamente no DOM
                    const currentText = this.text_.textContent;
                    const translated = translateBlockTextInDOM(currentText);
                    if (translated !== currentText) {
                        this.text_.textContent = translated;
                        // Força re-renderização
                        if (this.render) {
                            this.render();
                        }
                    }
                }
            }, 0);
        }
    },
    // Hook chamado após renderização
    render: function () {
        // Chama o render original se existir
        if (this.constructor.superClass_ && this.constructor.superClass_.prototype.render) {
            this.constructor.superClass_.prototype.render.call(this);
        }
        
        // Traduz o texto no DOM após renderização
        if (this.text_ && this.text_.textContent && this.type && this.type.startsWith('arduino_')) {
            const currentText = this.text_.textContent;
            const translated = translateBlockTextInDOM(currentText);
            if (translated !== currentText) {
                this.text_.textContent = translated;
            }
        }
    }
});

// Função auxiliar para traduzir texto no DOM
function translateBlockTextInDOM(text) {
    if (!text) return text;
    
    // Traduções diretas
    const translations = {
        'set pin': 'definir pino',
        'set digital pin': 'definir pino digital',
        'set pwm pin': 'definir pino pwm',
        'set servo pin': 'definir pino servo',
        'read digital pin': 'ler pino digital',
        'read analog pin': 'ler pino analógico',
        'attach interrupt pin': 'anexar interrupção pino',
        'detach interrupt pin': 'desanexar interrupção pino',
        'mode': 'modo',
        'out': 'saída',
        'executes': 'executa'
    };
    
    let translated = text;
    
    // Traduz frases completas primeiro
    translated = translated
        .replace(/set pin (\d+) mode (input|output|input-pullup)/gi, 'definir pino $1 modo $2')
        .replace(/set digital pin (\d+) out (high|low)/gi, 'definir pino digital $1 saída $2')
        .replace(/set pwm pin (\d+) out (\d+)/gi, 'definir pino pwm $1 saída $2')
        .replace(/set servo pin (\d+) out (\d+)/gi, 'definir pino servo $1 saída $2')
        .replace(/read digital pin (\d+)/gi, 'ler pino digital $1')
        .replace(/read analog pin (A\d+|\d+)/gi, 'ler pino analógico $1')
        .replace(/attach interrupt pin (\d+) mode (rising edge|falling edge|change edge|low) executes/gi, 'anexar interrupção pino $1 modo $2 executa')
        .replace(/detach interrupt pin (\d+)/gi, 'desanexar interrupção pino $1');
    
    // Traduz valores
    translated = translated
        .replace(/\binput\b/gi, 'entrada')
        .replace(/\boutput\b/gi, 'saída')
        .replace(/\binput-pullup\b/gi, 'entrada-pullup')
        .replace(/\bhigh\b/gi, 'alto')
        .replace(/\blow\b/gi, 'baixo')
        .replace(/\brising edge\b/gi, 'borda de subida')
        .replace(/\bfalling edge\b/gi, 'borda de descida')
        .replace(/\bchange edge\b/gi, 'mudar borda');
    
    return translated;
}

export default defineDynamicBlock;
